import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Microphone, Stop, CheckCircle, Camera } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ClockInDialog({ booking, onDone }) {
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(); const timerRef = useRef(); const chunksRef = useRef([]);
  const photoRef = useRef();

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((x) => x + 1), 1000);
      setTimeout(() => { if (recRef.current && recRef.current.state === "recording") stop(); }, 30000);
    } catch (e) { toast.error("Mic not available"); }
  };
  const stop = () => { recRef.current?.stop(); setRecording(false); };
  const onPhoto = (e) => { const f = e.target.files?.[0]; if (f) setPhoto(f); };
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (blob) fd.append("voice", new File([blob], "clockin.webm", { type: "audio/webm" }));
      await api.post(`/bookings/${booking.id}/clock-in`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      if (photo) {
        const pf = new FormData(); pf.append("photo", photo);
        await api.post(`/bookings/${booking.id}/clock-in-photo`, pf, { headers: { "Content-Type": "multipart/form-data" }});
      }
      toast.success("Clocked in!");
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 rounded-md bg-blue-50 border border-blue-200 space-y-3">
      <div className="font-semibold text-slate-900 flex items-center gap-2"><Microphone size={16}/> Clock in with proof (voice + photo, both optional)</div>
      <div className="flex items-center gap-2 flex-wrap">
        {!recording && !blob && <Button data-testid={`voice-record-${booking.id}`} size="sm" variant="outline" onClick={start}><Microphone size={14} className="mr-1"/> Voice</Button>}
        {recording && <Button data-testid={`voice-stop-${booking.id}`} size="sm" onClick={stop} className="bg-red-500 hover:bg-red-600"><Stop size={14} weight="fill" className="mr-1"/> Stop · {elapsed}s</Button>}
        {blob && <div className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={14} weight="fill"/> {(blob.size/1024).toFixed(0)} KB</div>}

        <input ref={photoRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPhoto} data-testid={`photo-input-${booking.id}`}/>
        <Button data-testid={`photo-btn-${booking.id}`} size="sm" variant="outline" onClick={()=>photoRef.current?.click()}><Camera size={14} className="mr-1"/> {photo ? "Photo ready" : "Selfie"}</Button>

        <Button data-testid={`clockin-submit-${booking.id}`} size="sm" onClick={submit} disabled={busy || recording} className="bg-blue-600 hover:bg-blue-700 ml-auto">
          {busy ? "Clocking in…" : "Clock in"}
        </Button>
      </div>
      {recording && <div className="text-xs text-slate-500">Speak briefly (max 30s), e.g. "Arrived at 8:55, all set."</div>}
    </div>
  );
}
